import { getPintMasters } from '@api/pint';
import { AppNoBodyPadding } from '@components/app-settings';
import { BreakLine } from '@components/breakline';
import { ColoredButton } from '@components/colored-elements';
import { ScrollSpyMuiTabs } from '@components/mui/tabs';
import { ScrollSpyBoundry } from '@components/scroll-spy';
import { vestResolver } from '@hookform/resolvers/vest';
import { useFormSnackbarError, usePreload } from '@lib/common';
import { ContentType } from '@lib/content';
import { AppBar, Box, Grid, Tab } from '@material-ui/core';
import {
  amber, blue, cyan, green, grey, lightBlue,
  lightGreen, orange, pink, purple, red,
  teal
} from '@material-ui/core/colors';
import CancelIcon from '@material-ui/icons/CancelRounded';
import PublishRoundedIcon from '@material-ui/icons/PublishRounded';
import SaveIcon from '@material-ui/icons/SaveRounded';
import { CompanyAssociatedDropDownPersonLoader } from 'pages/deals/deal-form/deal-relation-form-multi-party';
import { useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import ConentFormAdapter from './content.form.adapter';
import { ContentFormContext } from './content.form.context';
import { ContentBasicFormField } from './fields';
import { AdditionalInfoContentSection } from './sections/addtional-info-content.section';
import { ContentBasicSection } from './sections/basic-content-section';
import { CastandCrewSection } from './sections/cast_crew_section';
import { ContentInformationSection } from './sections/content-info.section';
import { MediaSection } from './sections/media_section';
import { OtherAttributesSection } from './sections/other-attributes-section';
import { SectionBadges } from './sections/section-badges';
import { TVSeriesSeasonInfo } from './sections/tv-series-season-info';
import { contentValidationSuite } from './validations';
import { PersonDropdownList, PersonDropdownRef } from 'pages/common/person-dropdown-list';
import { validateCastCrewList } from '@lib/common/cast-crew-validation.utils';
import VersionHistoryTable from '@components/version-history/version-history';
import { mockData } from './mock-data';

export const ContentForm = ({ defaultValues = null, edit = false, onFinish = null }) => {
 /*  const formValue = useMemo(() => {
    const v = edit && defaultValues ? ConentFormAdapter.toForm(defaultValues) : null;
    return v;
  }, [defaultValues]); */
  const formValue = useMemo(() => {
    if (!defaultValues) return null;

    return edit
      ? ConentFormAdapter.toForm(defaultValues)
      : defaultValues;
  }, [defaultValues, edit]);
  useEffect(() => {
      if (formValue) {
          form.reset(formValue);
      }
  }, [formValue]);
  const [formSumitted, setFormSumitted] = useState(false);
  const form = useForm({
    resolver: vestResolver(contentValidationSuite),
    ...(formValue
      ? {
          defaultValues: formValue,
        }
      : {}),
  });
  const initialSeasonDetailsLength = formValue?.season_numbers?.length;
  const personDropdownRef = useRef<PersonDropdownRef>(null);
  useEffect(() => {
    form.register('vtr_id');
  }, [form.register]);
  const formError = useFormSnackbarError();
  const history = useHistory();

  const submit = useCallback(() => {
    const persons = personDropdownRef.current?.getPersons?.() || [];
    
    // Custom error handler for cast crew validation
    const customErrorHandler = (errors: any) => {
      if (errors.cast_crew_duplicates) {
        // Get the detailed validation message
        const validation = validateCastCrewList(persons);
        if (!validation.isValid && validation.message) {
          // Show the detailed error message
          formError({
            cast_crew_duplicates: {
              message: validation.message
            }
          });
          return;
        }
      }
      // Handle other errors normally
      formError(errors);
    };

    form.handleSubmit(values => {
      const formData = {
        ...values,
        cast_crew: persons
      };
      onFinish && onFinish(ConentFormAdapter.fromForm(formData));
    }, customErrorHandler)();
  }, [form, formError, onFinish]);
  // Preload the masters before hand.
  usePreload(getPintMasters);

  const contentTypeSeries = form.watch('content_type', ContentType.SERIES) === ContentType.SERIES;
  const contentTypeWatch = form.watch('content_type');
  const contentKeywords = form.watch('content_keywords');
  let color = '';
  let label = '';
  if (contentTypeWatch == ContentType.SERIES) {
    label = 'TV Show Details';
    color = green[900];
  } else if (contentTypeWatch == ContentType.MOVIE) {
    label = 'MOVIE DETAILS';
    color = blue[900];
  } else if (contentTypeWatch == ContentType.BOOK) {
    label = 'BOOK DETAILS';
    color = cyan[900];
  } else if (contentTypeWatch == ContentType.PODCAST) {
    label = 'PODCAST DETAILS';
    color = grey[900];
  } else if (contentTypeWatch == ContentType.GAME) {
    label = 'GAME DETAILS';
    color = amber[900];
  } else if (contentTypeWatch == ContentType.FORMAT) {
    label = 'FORMAT DETAILS';
    color = purple[900];
  } else if (contentTypeWatch == ContentType.STORY) {
    label = 'STORY/SCRIPT DETAILS';
    color = orange[600];
  } else if (contentTypeWatch == ContentType.IP) {
    label = 'IP DETAILS';
    color = teal[600];
  } else if (contentTypeWatch == ContentType.MICROSERIES) {
    label = 'MICRO-SERIES DETAILS';
    color = pink[700];
  } else {
    label = 'TV Show Details';
    color = green[900];
  }
  // const history = useHistory();
  const redirectToSearch = useCallback(() => {
    if (history.location.search) {
      const returnTo = new URLSearchParams(history.location.search).get('returnTo');
      if (returnTo) {
        history.push(returnTo);
        return;
      }
    }
    history.push(`/content/search`);
  }, [history]);

  const isASeriesContent = contentTypeWatch == ContentType.SERIES;

  return (
    <FormProvider {...form}>
      <ContentFormContext
        editMode={edit}
        form={formValue}
        triggerSubmit={submit}
        actual={defaultValues}>
        <AppNoBodyPadding />
        <ScrollSpyBoundry defaultItem="basic-details">
          <div>
            <AppBar
              style={{
                position: 'sticky',
                top: 64,
                background: color,
              }}>
              <Grid container alignItems="center">
                <Grid item>
                  <ScrollSpyMuiTabs $small>
                    <Tab value="basic-details" label="Basic" />
                    <Tab value="additional-details" label="Info" />
                    <Tab value="content-info" label={label} />
                    <Tab value="content-associated-persons" label={"Cast & crew"} />
                    <Tab value="other-attributes" label={"Other Attributes"} />
                    {isASeriesContent && <Tab value="season-info" label={"Season Info"} />}
                    <Tab value="content-badges" label={"Badges"} />
                    <Tab value="content-media" label={"Media"} />
                  </ScrollSpyMuiTabs>
                </Grid>
                <Grid item xs />
                <Grid item>
                      <PublishContainer>
                        <ContentBasicFormField
                          name="content_publish_ready"
                          icon={<PublishRoundedIcon style={{ color: 'white' }} />}
                          hideEmptySelect
                          select
                          selectOptions={publishOptions}
                        />
                      </PublishContainer>
                    </Grid>
                <Grid item>
                  <ColoredButton
                    colored={red[500]}
                    size="small"
                    coloredHover={red[900]}
                    startIcon={<CancelIcon />}
                    variant="contained"
                    onClick={() => redirectToSearch()}>
                    Cancel
                  </ColoredButton>
                  &nbsp; &nbsp;
                  <ColoredButton
                    colored={contentTypeSeries ? lightBlue[700] : lightGreen[700]}
                    size="small"
                    coloredHover={contentTypeSeries ? lightBlue[900] : lightGreen[900]}
                    startIcon={<SaveIcon />}
                    variant="contained"
                    // disabled={rbac('Content_Save', '', 'Button')}
                    onClick={submit}>
                    Save
                  </ColoredButton>
                </Grid>
              </Grid>
            </AppBar>

            <Box px={2}>
              <br />
              <ContentBasicSection />
              <BreakLine n={4} />
              <AdditionalInfoContentSection edit={edit}/>
              <BreakLine n={3} />
              <ContentInformationSection />
              <BreakLine n={3} />
              <PersonDropdownList
                ref={personDropdownRef}
                defaultValues={formValue}
                name="cast_crew"
                title="Cast and Crew Details"
                subtitle='Information about Cast and Crew Members'
              />
              {/* <CastandCrewSection /> */}
              <BreakLine n={3} />
              <OtherAttributesSection isASeriesContent={isASeriesContent}/>
              <BreakLine n={3} />
              {contentTypeWatch == ContentType.SERIES && <TVSeriesSeasonInfo initialSeasonLen={initialSeasonDetailsLength}/>}
              <BreakLine n={3} />
              <SectionBadges />
              <BreakLine n={3} />
              <MediaSection />
              <BreakLine n={3} />
              {edit && (<><VersionHistoryTable/>
                <BreakLine n={6} /></>)}
            </Box>
          </div>
        </ScrollSpyBoundry>
      </ContentFormContext>
    </FormProvider>
  );
};
const listObj = (label: string, value: string | number) => ({ label, value });
const publishOptions = [
  // listObj('Auto Decide', 0), //REF:VSDI-10087
  listObj('Publish', 2),
  listObj('Unpublish', -1),
  listObj('Park', -2),
];
const PublishContainer = styled.div`
  margin-right: 10px;
  border-radius: 4px;
  background: ${amber[900]};
  box-shadow: 0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%),
    0px 1px 5px 0px rgb(0 0 0 / 12%);
  select {
    padding-top: 6px;
    padding-bottom: 6px;
    color: white;
    option {
      color: black;
    }
  }
  .MuiSelect-iconOutlined {
    color: white;
  }
`;
